import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";

import { cn } from "@src/lib/utils/cn";
import XIcon from "@src/lib/icons/12px/x";
import Text from "@src/lib/ui/Text";
import ScrollableFlexGrow from "@src/lib/ui/ScrollableFlexGrow";
import {
  DIALOG_CLOSE_BUTTON_TOP_CLASSNAME,
  DIALOG_DESCRIPTION_CLASSNAME,
  DIALOG_OVERLAY_CLASSNAME,
  DIALOG_TITLE_CLASSNAME,
  DIALOG_TITLE_VARIANTS,
  DialogContentLayoutContext,
  type DialogTitleSize,
  OverlayOpenContext,
  getDialogContentClassName,
  getDialogFooterClassName,
  getDialogHeaderClassName,
  getZIndexClass,
  useControllableOpen,
  useDialogContentLayout,
  useFrozenWhileClosed,
} from "@src/lib/ui/dialogShell";

const Dialog = ({
  open: openProp,
  defaultOpen,
  onOpenChange,
  ...props
}: React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Root
>): React.JSX.Element => {
  const [open, setOpen] = useControllableOpen({
    open: openProp,
    defaultOpen,
    onOpenChange,
  });
  return (
    <OverlayOpenContext.Provider value={open}>
      <DialogPrimitive.Root open={open} onOpenChange={setOpen} {...props} />
    </OverlayOpenContext.Provider>
  );
};
Dialog.displayName = "Dialog";

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(DIALOG_OVERLAY_CLASSNAME, className)}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    hideCloseButton?: boolean;
    /** When true, omit the default sr-only title so the dialog can render a visible `DialogTitle` (only one `DialogPrimitive.Title` may exist). */
    hideDefaultTitle?: boolean;
  }
>(
  (
    {
      className,
      children,
      hideCloseButton = false,
      hideDefaultTitle = false,
      ...props
    },
    ref,
  ) => {
    const { className: contentClassName, isContainer } =
      getDialogContentClassName(className);
    const { layout, descriptionCount, titleSize } = useDialogContentLayout(
      isContainer,
      !hideCloseButton,
    );

    // Keep the last open render on screen while the close animation runs.
    // A force-mounted panel stays mounted while closed, so it stays live.
    const frozenChildren = useFrozenWhileClosed(children);
    const renderedChildren = props.forceMount ? children : frozenChildren;

    return (
      <DialogPortal>
        <DialogOverlay className={getZIndexClass(contentClassName)} />
        <DialogPrimitive.Content
          ref={ref}
          data-testid="dialog-content"
          onClick={(e) => e.stopPropagation()}
          className={contentClassName}
          // Without a DialogDescription, opt out of aria-describedby the way
          // Radix documents rather than describing every dialog as "Dialog
          // content". A mounted DialogDescription wires itself up as usual,
          // and a caller's own aria-describedby (in props) still wins.
          {...(descriptionCount === 0 && { "aria-describedby": undefined })}
          {...props}
        >
          {!hideDefaultTitle && (
            <DialogPrimitive.Title className="sr-only">
              Dialog
            </DialogPrimitive.Title>
          )}
          <DialogContentLayoutContext.Provider value={layout}>
            {renderedChildren}
          </DialogContentLayoutContext.Provider>
          {!hideCloseButton && (
            <DialogPrimitive.Close
              className={cn(
                "absolute end-3 flex size-6 items-center justify-center rounded-md text-icon-secondary transition-colors before:absolute before:-inset-2 before:rounded-md hover:bg-state-hover focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:pointer-events-none data-[state=open]:bg-transparent data-[state=open]:text-icon-secondary",
                DIALOG_CLOSE_BUTTON_TOP_CLASSNAME[titleSize],
              )}
            >
              <XIcon className="size-4 text-icon-secondary" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  },
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.JSX.Element => {
  const layout = React.useContext(DialogContentLayoutContext);
  return (
    <div className={getDialogHeaderClassName(className, layout)} {...props} />
  );
};
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.JSX.Element => {
  const layout = React.useContext(DialogContentLayoutContext);
  return (
    <div className={getDialogFooterClassName(className, layout)} {...props} />
  );
};
DialogFooter.displayName = "DialogFooter";

const DialogTitle = ({
  children,
  className,
  size = "default",
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & {
  /** `large` steps up to the 20px display variant; there is no small size. */
  size?: DialogTitleSize;
}): React.JSX.Element => {
  // Lets DialogContent line the close button up with this title's first line.
  const { reportTitleSize } = React.useContext(DialogContentLayoutContext);
  React.useLayoutEffect(() => reportTitleSize(size), [reportTitleSize, size]);
  return (
    <DialogPrimitive.Title asChild>
      <Text
        variant={DIALOG_TITLE_VARIANTS[size]}
        multiline
        className={cn(DIALOG_TITLE_CLASSNAME, className)}
        {...props}
      >
        {children}
      </Text>
    </DialogPrimitive.Title>
  );
};
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = ({
  children,
  className,
  ...props
}: React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Description
>): React.JSX.Element => {
  const { registerDescription } = React.useContext(DialogContentLayoutContext);
  React.useLayoutEffect(() => registerDescription(), [registerDescription]);
  return (
    <DialogPrimitive.Description asChild>
      <Text
        variant="compact-description"
        multiline
        className={cn(DIALOG_DESCRIPTION_CLASSNAME, className)}
        {...props}
      >
        {children}
      </Text>
    </DialogPrimitive.Description>
  );
};
DialogDescription.displayName = DialogPrimitive.Description.displayName;

const DialogSidebar = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof ScrollableFlexGrow>
>(({ className, ...props }, ref) => (
  <ScrollableFlexGrow
    ref={ref}
    flex="none"
    className={cn("w-[280px] shrink-0 border-r border-gray-3", className)}
    innerClassName="p-[24px]"
    {...props}
  />
));
DialogSidebar.displayName = "DialogSidebar";

const DialogBody = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-1 self-stretch flex-col bg-gray-1", className)}
    {...props}
  />
));
DialogBody.displayName = "DialogBody";

const DialogBodyHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex h-[54px] shrink-0 items-center justify-between border-b border-gray-3 bg-white py-[10px]",
      className,
    )}
    {...props}
  />
));
DialogBodyHeader.displayName = "DialogBodyHeader";

const DialogActions = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex shrink-0 items-center justify-end gap-[12px] pr-[24px]",
      className,
    )}
    {...props}
  >
    {children}
  </div>
));
DialogActions.displayName = "DialogActions";

const DialogBodyScrollarea = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof ScrollableFlexGrow>
>(({ className, ...props }, ref) => (
  <ScrollableFlexGrow ref={ref} className={cn(className)} {...props} />
));
DialogBodyScrollarea.displayName = "DialogBodyScrollarea";

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogSidebar,
  DialogBody,
  DialogBodyHeader,
  DialogActions,
  DialogBodyScrollarea,
};
